import type { SupabaseClient } from "@supabase/supabase-js";
import type {
  AuditRepository,
  ExpectedRevision,
  Page,
  PageRequest,
  PersistedAudit,
  RepositoryResult,
} from "../contracts";
import type { Database } from "../database.generated";
import type { AuditEngagementRow } from "../database.types";
import { mapAuditRow } from "../mappers";
import { auditDetailsCodec } from "./codecs";
import { revisionConflict } from "./errors";
import {
  createIdPage,
  cursorId,
  databaseFailure,
  notFound,
  validationFailure,
} from "./repository-helpers";

export class SupabaseAuditRepository implements AuditRepository {
  constructor(private readonly client: SupabaseClient<Database>) {}

  async getById(id: string): Promise<RepositoryResult<PersistedAudit>> {
    const { data, error } = await this.client.from("audit_engagements").select("*").eq("id", id).maybeSingle();
    if (error) return databaseFailure(error, "The audit could not be read.");
    if (!data) return notFound("Audit");
    return mapAuditRow(data as AuditEngagementRow);
  }

  async listForOrganisation(
    organisationId: string,
    page: PageRequest,
  ): Promise<RepositoryResult<Page<PersistedAudit>>> {
    const order = `id.asc|organisation:${organisationId}`;
    const after = cursorId(page, order);
    if (!after.ok) return after;
    let query = this.client
      .from("audit_engagements")
      .select("*")
      .eq("organisation_id", organisationId)
      .order("id", { ascending: true })
      .limit(page.limit + 1);
    if (after.value) query = query.gt("id", after.value);
    const { data, error } = await query;
    if (error) return databaseFailure(error, "Audits could not be listed.");
    return createIdPage(data ?? [], page, (row) => mapAuditRow(row as AuditEngagementRow), order);
  }

  async create(
    record: Omit<PersistedAudit, "revision" | "updatedAt">,
  ): Promise<RepositoryResult<PersistedAudit>> {
    const details = auditDetailsCodec.decode(record.details, record.details.schemaVersion);
    if (!details.ok) return validationFailure(details.error.message);
    const { data, error } = await this.client
      .from("audit_engagements")
      .insert({
        id: record.id,
        organisation_id: record.organisationId,
        label: record.label ?? null,
        audit_type: record.auditType,
        status: record.status,
        delivery_status: record.deliveryStatus,
        follow_up_status: record.followUpStatus,
        rehearsal: record.rehearsal,
        delivered_at: record.deliveredAt ?? null,
        delivered_report_version_id: record.deliveredReportVersionId ?? null,
        details_schema_version: details.value.schemaVersion,
        details: details.value,
        archived_at: record.archivedAt ?? null,
      })
      .select("*")
      .single();
    if (error) return databaseFailure(error, "The audit could not be created.");
    return mapAuditRow(data as AuditEngagementRow);
  }

  async update(
    id: string,
    changes: Partial<Omit<PersistedAudit, "id" | "organisationId" | "revision" | "updatedAt">>,
    options: ExpectedRevision,
  ): Promise<RepositoryResult<PersistedAudit>> {
    const row: Database["public"]["Tables"]["audit_engagements"]["Update"] = {};
    if ("label" in changes) row.label = changes.label ?? null;
    if (changes.auditType !== undefined) row.audit_type = changes.auditType;
    if (changes.status !== undefined) row.status = changes.status;
    if (changes.deliveryStatus !== undefined) row.delivery_status = changes.deliveryStatus;
    if (changes.followUpStatus !== undefined) row.follow_up_status = changes.followUpStatus;
    if (changes.rehearsal !== undefined) row.rehearsal = changes.rehearsal;
    if ("deliveredAt" in changes) row.delivered_at = changes.deliveredAt ?? null;
    if ("deliveredReportVersionId" in changes) {
      row.delivered_report_version_id = changes.deliveredReportVersionId ?? null;
    }
    if (changes.details !== undefined) {
      const details = auditDetailsCodec.decode(changes.details, changes.details.schemaVersion);
      if (!details.ok) return validationFailure(details.error.message);
      row.details_schema_version = details.value.schemaVersion;
      row.details = details.value;
    }
    if ("archivedAt" in changes) row.archived_at = changes.archivedAt ?? null;
    if (!Object.keys(row).length) return validationFailure("At least one audit change is required.");

    const { data, error } = await this.client
      .from("audit_engagements")
      .update(row)
      .eq("id", id)
      .eq("revision", options.expectedRevision)
      .select("*")
      .maybeSingle();
    if (error) return databaseFailure(error, "The audit could not be updated.");
    if (data) return mapAuditRow(data as AuditEngagementRow);
    const current = await this.getById(id);
    return current.ok ? { ok: false, error: revisionConflict() } : current;
  }

  archive(id: string, archivedAt: string, options: ExpectedRevision): Promise<RepositoryResult<PersistedAudit>> {
    return this.update(id, { archivedAt }, options);
  }
}
